import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import PropTypes from 'prop-types'

import { orderItemAttachmentsPath, useDeleteOrderItemAttachmentMutation } from '@/app/services/orderAttachments'
import { useGetOrderItemsQuery } from '@/app/services/orderItems'
import { uploadAttachmentFile } from '@/app/services/uploadAttachmentFile'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { useSignIn } from '@/common/hooks/useSignIn'

import AttachmentsPanel from './AttachmentsPanel'
import { freshDownloadUri } from './freshDownloadUri'

// Stable fallback, so an item without rows doesn't re-render the panel on every items update.
const NO_ATTACHMENTS = []

const findAttachments = (items, orderItemId) =>
    items?.find(({ id }) => id === orderItemId)?.attachments ?? NO_ATTACHMENTS

/**
 * OrderItemAttachments - Documents on one quote item of a web store quote. The rows ride on the
 * quote items list (the server adds the item's public attachments), so uploads and deletes refresh
 * that list rather than a separate one. The caller gates it behind the `showQuoteAttachments` flag.
 *
 * Reads the quote id from the route and the organisation from the store.
 *
 * @param {Object} props
 * @param {string} props.orderItemId
 */
const OrderItemAttachments = ({ orderItemId }) => {
    const { t } = useTranslation()
    const { orderId } = useParams()
    const { isAuthenticated } = useKindeAuth()
    const signIn = useSignIn()
    const organisationId = useSelector(selectOrganisationId)
    const parent = { organisationId, orderId, orderItemId }

    const { attachments, fulfilledTimeStamp, isLoading, refetch } = useGetOrderItemsQuery(
        { organisationId, orderId },
        {
            selectFromResult: ({ data, fulfilledTimeStamp, isLoading }) => ({
                attachments: findAttachments(data, orderItemId),
                fulfilledTimeStamp,
                isLoading,
            }),
        }
    )

    const [deleteAttachment] = useDeleteOrderItemAttachmentMutation()

    const resolveDownloadUri = (attachment) =>
        freshDownloadUri({
            attachment,
            fetchedAt: fulfilledTimeStamp,
            refetchRows: async () => {
                const { data, error } = await refetch()
                if (error) throw error
                return findAttachments(data, orderItemId)
            },
        })

    return (
        <AttachmentsPanel
            attachments={attachments}
            canUpload={Boolean(isAuthenticated)}
            emptyMessage={t('No attachments on this part yet.')}
            isLoading={isLoading}
            resolveDownloadUri={resolveDownloadUri}
            title={t('Part attachments')}
            onDelete={(documentId) => deleteAttachment({ ...parent, documentId }).unwrap()}
            onSignIn={signIn}
            onUploadFile={(file, options) =>
                uploadAttachmentFile({ path: orderItemAttachmentsPath(parent), file, ...options })
            }
            onUploadsSettled={refetch}
        />
    )
}

OrderItemAttachments.propTypes = {
    orderItemId: PropTypes.string.isRequired,
}

export default OrderItemAttachments
